import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import { Button, ContentLoader, List } from 'shared/components';
import { displayValue } from 'shared/helpers';
import { fetchAccountLicenseUserGroups } from '../../../actions';
import '../../styles.scss';

const PAGE_SIZE = 20;

const UserGroupsTab = ({ accountId }) => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([]);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    let isStale = false;
    setLoading(true);
    setError(false);
    fetchAccountLicenseUserGroups(accountId, {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      orderBy: currentSort,
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        const totalCount = Array.isArray(payload)
          ? results.length
          : get(payload, 'count', results.length);
        if (!isStale) {
          setGroups(results);
          setCount(totalCount);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!isStale) {
          setError(true);
          setGroups([]);
          setCount(0);
          setLoading(false);
        }
      });
  }, [accountId, currentSort, page, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const columns = [
    {
      Header: t('Name'),
      accessor: 'name',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('Code'),
      accessor: 'code',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('Description'),
      accessor: 'description',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('Metadata'),
      id: 'metadata',
      accessor: (row) => get(row, 'metadata'),
      Cell: (cellData) => {
        const value = cellData.value;
        if (!value || Object.keys(value).length === 0) {
          return displayValue(null, '{}');
        }
        return displayValue(JSON.stringify(value));
      },
      maxWidth: 350,
    },
  ];

  if (loading) {
    return <ContentLoader text={t('Loading user groups')} />;
  }

  if (error) {
    return (
      <div className="CustomerAccount-error">
        <p>{t('Unable to load user groups for this account')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!groups.length) {
    return (
      <div className="CustomerAccount-empty">
        {t('No user groups for this account')}
      </div>
    );
  }

  const pages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="CustomerAccount-list">
      <List
        data={groups}
        columns={columns}
        page={page}
        pages={pages}
        manual
        minRows={groups.length || 5}
        showPagination={pages > 1}
        loading={loading}
        onPageChange={(newPage) => setPage(newPage)}
        onSortedChange={(newSorted) => setCurrentSort(newSorted)}
        defaultSorted={currentSort}
        defaultPageSize={PAGE_SIZE}
      />
    </div>
  );
};

UserGroupsTab.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default UserGroupsTab;
